import { Alert, Box, Button, SxProps, Theme, useTheme } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { RoundedBox } from "@knowfully/ui.common";
import { useState } from "react";
import { FormattedMessage as T } from "react-intl";
import { useQuery } from "react-query";
import useGlobalContext from "hooks/globalData";
import authStore from "stores/authStore";
import contextStore from "stores/contextStore";
import { isPendingInvitationsBannerSuppressed } from "utils/pendingInvitationsBannerStorage";
import ResendAllInvitationsDialog from "./ResendAllInvitationsDialog";

const useStyles = (theme: Theme): { [key: string]: SxProps } => ({
  alert: {
    alignItems: "center",
  },
  title: {
    fontWeight: "bold",
    color: theme.palette.text.primary,
  },
  description: {
    color: theme.palette.text.secondary,
  },
});

export default function PendingInvitationsBanner() {
  const styles = useStyles(useTheme());
  const { organizationUserService } = useGlobalContext();
  const { organization } = contextStore.useState();
  const adminUserId = authStore.useState((s) => s.id);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data } = useQuery(
    ["organizationUserService.getPendingInvitations", organization?.id],
    () => organizationUserService.getPendingInvitations(),
    { enabled: !!organization?.id }
  );

  const pendingUserCount = data?.data?.pendingUserCount ?? 0;

  const ready = !!adminUserId && !!organization?.id;

  const showBanner =
    ready &&
    pendingUserCount > 0 &&
    !isPendingInvitationsBannerSuppressed(adminUserId, organization.id);

  return (
    <>
      {showBanner && (
        <RoundedBox>
          <Alert
            severity="info"
            icon={<MailOutlineIcon />}
            sx={styles.alert}
            action={
              <Button
                variant="contained"
                color="primary"
                startIcon={<MailOutlineIcon />}
                onClick={() => setDialogOpen(true)}
              >
                <T id="resendAllInvitations" />
              </Button>
            }
          >
            <Box sx={styles.title}>
              <T
                id="pendingInvitationsBannerTitle"
                values={{ usersCount: pendingUserCount }}
              />
            </Box>
            <Box sx={styles.description}>
              <T
                id="pendingInvitationsBannerDescription"
                values={{ usersCount: pendingUserCount }}
              />
            </Box>
          </Alert>
        </RoundedBox>
      )}

      <ResendAllInvitationsDialog
        open={dialogOpen}
        pendingUserCount={pendingUserCount}
        close={() => setDialogOpen(false)}
      />
    </>
  );
}
